import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, Trash2, Check, CheckCheck, Pencil } from "lucide-react";
import { classes } from "@/data/classes";
import { useDbClasses } from "@/hooks/useDbClasses";
import { useClassLabelResolver } from "@/lib/classLabels";
import {
  resolveEnrollmentPeriodAndScheduleOption,
  deriveWeekday,
  isFullyRefunded,
  type EnrollmentPeriod,
} from "@/lib/enrollment";
import { PeriodTabStrip } from "./PeriodTabStrip";
import {
  INTEREST_TAB,
  WINTER_TAB,
  OCTOBER_TAB,
  isOctoberOnlyLabel,
  registrationTabLabel,
  type RegistrationTabKey,
} from "@/lib/registrationPeriodNav";

import {
  resolveAllPeriods,
  isReviewBucket,
  REVIEW_BUCKETS,
  type ReviewBucket,
} from "@/lib/registrationSession";
import { friendlyRegistrationError } from "@/lib/registrationErrors";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { indexResolutions, findResolution, describeResolution, isFinanciallyOpen, type SessionResolution } from "@/lib/sessionResolutions";
import { supabase as sessionResDb } from "@/integrations/supabase/client";
import EditRegistrationDialog, { EditMode, EditableRegistration } from "./EditRegistrationDialog";

interface InterestSubmission {
  id: string;
  class_id: string;
  adult_name: string;
  email: string;
  child_name: string;
  child_age: number;
  topics: string | null;
  created_at: string;
  selected_session_label?: string | null;
  enrollment_period?: string | null;
  schedule_option?: string | null;
  /**
   * Structured, non-exclusive option selections from
   * `class_interest_options`. A child may be interested in several options
   * (e.g. Middle School D&D + Dad & Me D&D) on ONE interest record.
   */
  option_labels?: string[];
  weekday?: string | null;
}


interface StripePayment {
  /** Legacy row key (Stripe session when present). Display/marker use only. */
  id: string;
  /** Canonical class_payments.id UUID — the ONLY identifier safe for writes. */
  registration_uuid: string;
  stripe_session_id?: string | null;
  pending_registration_id?: string | null;
  is_test_record?: boolean | null;
  refunded_amount_cents?: number | null;
  removed_at?: string | null;
  operational_exception?: string | null;
  operational_exception_note?: string | null;
  canonical_session_label?: string | null;
  email: string;
  amount: number;
  original_price_cents?: number | null;
  discount_cents?: number | null;
  discount_code?: string | null;
  is_complimentary?: boolean | null;
  complimentary_reason?: string | null;
  currency: string;
  status: string;
  created: number;
  class_id: string;
  adult_name: string;
  child_name: string;
  child_age: string;
  selected_sessions: string;
  children?: Array<{
    first_name: string;
    last_name: string | null;
    age: number | null;
    session_label: string | null;
    grade: string | null;
    amount: number;
  }>;
}

const LEGACY_MULTI_CHILD_RE = /(\band\b|&|,|\/)/i;
const looksLikeLegacyMultiChild = (name: string) =>
  !!name && LEGACY_MULTI_CHILD_RE.test(name);

interface Props {
  focusPayment?: string | null;
  focusInterest?: string | null;
  focusClass?: string | null;
  onFocusHandled?: () => void;
}


const formatAmount = (a: number) => `$${(a / 100).toFixed(2)}`;
const formatDate = (t: number) => new Date(t * 1000).toLocaleDateString();

const parseSessionLabels = (s: string | null | undefined): string[] => {
  if (!s) return [];
  try {
    const arr = JSON.parse(s);
    if (Array.isArray(arr)) return arr.map(String);
    return [String(arr)];
  } catch {
    return [String(s)];
  }
};

// Display: show the raw labels so admins still see e.g. "Wednesdays (August)".
const displaySessions = (s: string | null | undefined): string => {
  const labels = parseSessionLabels(s);
  return labels.length ? labels.join(", ") : "—";
};

// Primary order is owned by the shared nav helper; this list only controls
// which period views exist and in what order their content renders.
const PERIOD_ORDER: RegistrationTabKey[] = [
  "Fall Semester", OCTOBER_TAB, WINTER_TAB, "Spring Semester",
  "May", "June", "July", "August",
  ...REVIEW_BUCKETS,
];


const markerKey = (type: "payment" | "interest", id: string) => `${type}:${id}`;

export default function RegistrationsPanel({ focusPayment, focusInterest, focusClass, onFocusHandled }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<InterestSubmission[]>([]);
  const [payments, setPayments] = useState<StripePayment[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [readMarkers, setReadMarkers] = useState<Set<string>>(new Set());
  const { resolve: resolveClass } = useClassLabelResolver();
  const [editMode, setEditMode] = useState<EditMode | null>(null);
  const [editInitial, setEditInitial] = useState<EditableRegistration | null>(null);
  const [tab, setTab] = useState("payments");
  const [paymentsPeriod, setPaymentsPeriod] = useState<RegistrationTabKey | null>(null);
  const [interestPeriod, setInterestPeriod] = useState<RegistrationTabKey | null>(null);
  const [assignTarget, setAssignTarget] = useState<StripePayment | null>(null);
  const [assignLabel, setAssignLabel] = useState("");
  const [removeTarget, setRemoveTarget] = useState<StripePayment | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchPayments();
    fetchReadMarkers();
  }, []);

  useEffect(() => {
    if (focusPayment && payments.length) {
      // Callers may deep-link with either the legacy row key (Stripe session id)
      // or the canonical registration uuid, so accept both.
      const p = payments.find((x) => x.id === focusPayment || x.registration_uuid === focusPayment);
      if (p) {
        setTab("payments");
        openEditPayment(p);
        onFocusHandled?.();
      }
    }
  }, [focusPayment, payments]);

  useEffect(() => {
    if (focusInterest && submissions.length) {
      const s = submissions.find((x) => x.id === focusInterest);
      if (s) {
        setTab("interest");
        openEditInterest(s);
        onFocusHandled?.();
      }
    }
  }, [focusInterest, submissions]);

  // Class-level deep link: open the paid-registrations period tab that holds
  // this class and scroll its group into view. Read-only navigation.
  useEffect(() => {
    if (!focusClass || !payments.length) return;
    const target = resolveClass(focusClass).canonicalKey || focusClass;
    const period = payView.periods.find((p) => !!payView.buckets[p]?.classes[target]);
    if (!period) return;
    setTab("payments");
    setPaymentsPeriod(period);
    const t = setTimeout(() => {
      document
        .getElementById(`payclass-${target}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    onFocusHandled?.();
    return () => clearTimeout(t);
  }, [focusClass, payments]);

  const fetchReadMarkers = async () => {
    const { data } = await supabase.from("admin_read_markers").select("item_type,item_id");
    if (data) setReadMarkers(new Set(data.map((m: any) => `${m.item_type}:${m.item_id}`)));
  };

  const fetchSubmissions = async () => {
    const { data } = await supabase
      .from("class_interest")
      .select("*")
      .gte("created_at", "2026-03-20T00:00:00Z")
      .order("created_at", { ascending: false });
    if (data) {
      const rows = data as InterestSubmission[];
      // Attach structured multi-option selections so one child can appear in
      // each option bucket they chose without duplicating the child record.
      const { data: sel } = await (supabase as any)
        .from("class_interest_options")
        .select("interest_id,display_label")
        .in("interest_id", rows.map((r) => r.id));
      const byInterest = new Map<string, string[]>();
      for (const row of (Array.isArray(sel) ? sel : []) as any[]) {
        const list = byInterest.get(row.interest_id) ?? [];
        if (row.display_label && !list.includes(row.display_label)) list.push(row.display_label);
        byInterest.set(row.interest_id, list);
      }
      setSubmissions(rows.map((r) => ({ ...r, option_labels: byInterest.get(r.id) ?? [] })));
    }
    setLoadingData(false);
  };

  const fetchPayments = async () => {
    setLoadingPayments(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-stripe-payments");
      if (error) throw error;
      const all: StripePayment[] = data?.payments || [];
      const cutoff = new Date("2026-03-20T00:00:00Z").getTime() / 1000;
      setPayments(all.filter((p) => p.created >= cutoff));
    } catch (err: any) {
      toast({ title: "Error loading payments", description: err.message ?? "Could not fetch.", variant: "destructive" });
    }
    setLoadingPayments(false);
  };

  const markAsRead = async (type: "payment" | "interest", id: string) => {
    const key = markerKey(type, id);
    if (readMarkers.has(key)) return;
    setReadMarkers((prev) => new Set(prev).add(key));
    await supabase.from("admin_read_markers").insert({ item_type: type, item_id: id, read_by: user?.id ?? null });
  };

  const markAsUnread = async (type: "payment" | "interest", id: string) => {
    const key = markerKey(type, id);
    if (!readMarkers.has(key)) return;
    setReadMarkers((prev) => {
      const n = new Set(prev);
      n.delete(key);
      return n;
    });
    await supabase.from("admin_read_markers").delete().eq("item_type", type).eq("item_id", id);
  };

  const markAllAsRead = async () => {
    const all: { type: "payment" | "interest"; id: string }[] = [
      ...payments.map((p) => ({ type: "payment" as const, id: p.id })),
      ...submissions.map((s) => ({ type: "interest" as const, id: s.id })),
    ];
    const newOnes = all.filter((i) => !readMarkers.has(markerKey(i.type, i.id)));
    if (!newOnes.length) return;
    setReadMarkers((prev) => {
      const n = new Set(prev);
      newOnes.forEach((i) => n.add(markerKey(i.type, i.id)));
      return n;
    });
    await supabase
      .from("admin_read_markers")
      .insert(newOnes.map((i) => ({ item_type: i.type, item_id: i.id, read_by: user?.id ?? null })));
    toast({ title: `Marked ${newOnes.length} as read` });
  };

  const openEditPayment = (p: StripePayment) => {
    let selected: string[] = [];
    if (p.selected_sessions) {
      try {
        const arr = JSON.parse(p.selected_sessions);
        if (Array.isArray(arr)) selected = arr;
      } catch { /* ignore */ }
    }
    setEditInitial({
      classId: p.class_id,
      adultName: p.adult_name || "",
      email: p.email || "",
      childName: p.child_name || "",
      childAge: p.child_age || "",
      selectedSessions: selected,
    });
    setEditMode({ kind: "payment", stripeSessionId: p.stripe_session_id ?? p.id });
  };

  const openEditInterest = (s: InterestSubmission) => {
    setEditInitial({
      classId: s.class_id,
      adultName: s.adult_name,
      email: s.email,
      childName: s.child_name,
      childAge: s.child_age,
      topics: s.topics,
    });
    setEditMode({ kind: "interest", rowId: s.id });
  };

  const handleEditSaved = (updated: EditableRegistration) => {
    if (!editMode) return;
    if (editMode.kind === "payment") {
      const sid = editMode.stripeSessionId;
      setPayments((prev) =>
        prev.map((p) => p.id === sid ? {
          ...p,
          class_id: updated.classId,
          adult_name: updated.adultName,
          email: updated.email,
          child_name: updated.childName,
          child_age: String(updated.childAge),
          selected_sessions: JSON.stringify(updated.selectedSessions ?? []),
        } : p)
      );
    } else {
      const rid = editMode.rowId;
      setSubmissions((prev) =>
        prev.map((s) => s.id === rid ? {
          ...s,
          class_id: updated.classId,
          adult_name: updated.adultName,
          email: updated.email,
          child_name: updated.childName,
          child_age: typeof updated.childAge === "number" ? updated.childAge : parseInt(String(updated.childAge), 10),
          topics: updated.topics ?? s.topics,
        } : s)
      );
    }
  };

  const deleteInterest = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    const { error } = await supabase.from("class_interest").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      toast({ title: "Entry deleted" });
    }
  };

  // Manually assign a legacy interest record to a specific session/day.
  // Uses the shared resolver so the assigned row groups identically to
  // brand-new submissions. Preserves the original row — no history rewrite.
  const assignInterestSession = async (sub: InterestSubmission, sessionLabel: string) => {
    const resolved = resolveEnrollmentPeriodAndScheduleOption(sessionLabel);
    const wk = deriveWeekday(sessionLabel);
    const patch = {
      selected_session_label: sessionLabel,
      enrollment_period: resolved.period,
      schedule_option: resolved.option ?? wk,
      weekday: wk,
    };
    const { error } = await supabase.from("class_interest").update(patch).eq("id", sub.id);
    if (error) {
      toast({ title: "Could not assign", description: error.message, variant: "destructive" });
      return;
    }
    // Keep structured selections in sync with the manual assignment.
    const label = resolved.option ?? wk;
    if (label) {
      await (supabase as any).from("class_interest_options").delete().eq("interest_id", sub.id);
      await (supabase as any).from("class_interest_options").insert({
        interest_id: sub.id,
        class_id: sub.class_id,
        display_label: label,
      });
    }
    setSubmissions((prev) =>
      prev.map((s) =>
        s.id === sub.id ? { ...s, ...patch, option_labels: label ? [label] : [] } : s,
      ),
    );
    toast({ title: "Session assigned", description: sessionLabel });
  };



  // Remove a registration from active views. ALWAYS keyed by the internal
  // registration UUID — never the Stripe Checkout Session ID (`cs_...`),
  // which is a text identifier and cannot be cast to uuid.
  const removeRegistration = async (payment: StripePayment, force = false) => {
    const registrationUuid = payment.registration_uuid;
    const trace = {
      registration_uuid: registrationUuid,
      stripe_session_id: payment.stripe_session_id ?? null,
      payment_id: registrationUuid,
      class_id: payment.class_id,
      parent_name: payment.adult_name,
    };
    console.info("[registration-delete] rpc-request", trace);
    setBusy(true);
    const { data, error } = await supabase.rpc("admin_remove_test_registration", {
      _registration_uuid: registrationUuid,
      _reason: force ? "Admin confirmed removal" : "Removed as test registration",
      _force: force,
    });
    setBusy(false);
    if (error) {
      console.error("[registration-delete] rpc-error", { ...trace, error });
      toast({
        title: "Could not remove this registration",
        description: friendlyRegistrationError(error),
        variant: "destructive",
      });
      return;
    }
    const result = data as { ok?: boolean; message?: string; ledger_entries_voided?: number } | null;
    console.info("[registration-delete] rpc-response", { ...trace, result });
    if (result && result.ok === false) {
      toast({
        title: "Live payment protected",
        description:
          result.message ??
          "This is a live paid registration. Please refund or void it instead.",
        variant: "destructive",
      });
      return;
    }
    setPayments((prev) => prev.filter((p) => p.registration_uuid !== registrationUuid));
    setRemoveTarget(null);
    toast({
      title: "Registration removed from active views",
      description: `Seat released. ${result?.ledger_entries_voided ?? 0} ledger entr${
        (result?.ledger_entries_voided ?? 0) === 1 ? "y" : "ies"
      } voided. The source record is preserved for audit.`,
    });
  };

  // Assign a canonical session to a registration that never recorded one.
  const assignRegistrationSession = async (payment: StripePayment, label: string) => {
    const paymentId = payment.registration_uuid;
    if (!paymentId || !label.trim()) {
      toast({
        title: "Could not assign this session",
        description:
          "We could not update this registration because its internal record could not be identified.",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("admin_assign_registration_session", {
      _payment_id: paymentId,
      _session_label: label.trim(),
      _source: "manual_admin",
    });
    setBusy(false);
    if (error) {
      console.error("[registrations] assign session failed", { paymentId, error });
      toast({
        title: "Could not assign this session",
        description: friendlyRegistrationError(error),
        variant: "destructive",
      });
      return;
    }
    setPayments((prev) =>
      prev.map((p) =>
        p.registration_uuid === paymentId
          ? { ...p, selected_sessions: JSON.stringify([label.trim()]), canonical_session_label: label.trim() }
          : p,
      ),
    );
    setAssignTarget(null);
    setAssignLabel("");
    toast({ title: "Session assigned", description: label.trim() });
  };

  const getClassName = (id: string) => resolveClass(id).title;

  const unreadPay = payments.filter((p) => !readMarkers.has(markerKey("payment", p.id))).length;
  const unreadInt = submissions.filter((s) => !readMarkers.has(markerKey("interest", s.id))).length;
  const totalUnread = unreadPay + unreadInt;

  // ---------------------------------------------------------------------
  // Operational bucketing for paid registrations.
  // period → class → schedule option, with SEAT counts that are
  // de-duplicated by child + class + resolved period. Two preserved source
  // records for the same child/class/period are one operational seat.
  // Fully refunded / removed records stay preserved but are routed to
  // Historical so they contribute zero operational seats.
  // ---------------------------------------------------------------------
  type PayRow = { payment: StripePayment; reasonText: string | null; historical: boolean };
  type PayBucket = {
    period: RegistrationTabKey;
    classes: Record<string, {
      className: string;
      groupKey: string;
      classIdRaw: string;
      isUnknown: boolean;
      options: Record<string, PayRow[]>;
    }>;
    seats: Set<string>;
    total: number;
  };

  const dbClasses = useDbClasses();
  const dbSessionsBySlug = useMemo(() => {
    const m = new Map<string, { label: string; dates: string }[]>();
    for (const c of dbClasses) {
      const slug = c.id.replace(/^db:/, "");
      m.set(slug, (c.sessions ?? []).map((s) => ({ label: s.label, dates: s.dates ?? "" })));
    }
    return m;
  }, [dbClasses]);

  const [sessionResolutions, setSessionResolutions] = useState<SessionResolution[]>([]);
  useEffect(() => {
    let alive = true;
    (sessionResDb as any)
      .from("registration_session_resolutions")
      .select("class_payment_id, original_session_label, resolution_status, reason, historical_period, offering_ran, financial_disposition, admin_note, resolved_at")
      .then(({ data }: { data: SessionResolution[] | null }) => { if (alive && data) setSessionResolutions(data); });
    return () => { alive = false; };
  }, []);
  const resolutionIndex = useMemo(() => indexResolutions(sessionResolutions), [sessionResolutions]);

  const payView = useMemo(() => {
    const buckets: Partial<Record<RegistrationTabKey, PayBucket>> = {};
    const ensureBucket = (period: RegistrationTabKey): PayBucket => {
      const b = buckets[period] ?? { period, classes: {}, seats: new Set<string>(), total: 0 };
      buckets[period] = b;
      return b;
    };

    for (const p of payments) {
      const rawId = p.class_id || "unknown";
      const label = resolveClass(rawId);
      // Same canonical eligibility predicate capacity uses: a fully refunded
      // or removed registration is history, never an operational seat.
      const notOperational =
        !!p.removed_at ||
        isFullyRefunded({
          amount_paid: p.amount ?? 0,
          refunded_amount_cents: p.refunded_amount_cents ?? 0,
        });
      // Preserved operational exception (e.g. August Chess was never offered).
      // The record keeps every field; only its non-semester periods are pulled
      // out of the active roster into "Needs Resolution".
      const exception = p.operational_exception ?? null;

      const resolved = resolveAllPeriods({
        paymentId: p.registration_uuid,
        stripeSessionId: p.stripe_session_id ?? null,
        classIdRaw: rawId,
        classIsUnknown: label.isUnknown,
        sessionLabels: parseSessionLabels(p.selected_sessions),
        canonicalSessionLabel: p.canonical_session_label ?? null,
        email: p.email,
        adultName: p.adult_name,
        childName: p.child_name,
        amountCents: p.amount,
        isTestRecord: p.is_test_record ?? false,
        createdAt: p.created ? new Date(p.created * 1000) : null,
        currentClassSessions: label.slug
          ? (dbSessionsBySlug.get(label.slug) ?? null)
          : null,
      });

      const groupKey = label.canonicalKey || rawId;
      const childNames = (p.children ?? []).length
        ? (p.children ?? []).map((c) => `${c.first_name} ${c.last_name ?? ""}`.trim())
        : [p.child_name || p.registration_uuid];

      const seen = new Set<string>();
      for (const r of resolved) {
        // October month-only offerings resolve to "Fall Semester" in the
        // canonical resolver (which drives capacity). For DISPLAY only, they
        // get their own tab, keyed by the exact purchased session label.
        const octoberOnly =
          !notOperational && r.period === "Fall Semester" &&
          (isOctoberOnlyLabel(r.label) || isOctoberOnlyLabel(r.sessionDates));
        const optKey = octoberOnly ? (r.label ?? "October").trim() : (r.option ?? "—");
        const naturalKey: RegistrationTabKey = notOperational
          ? "Historical"
          : octoberOnly
            ? OCTOBER_TAB
            : (r.period ?? (r.bucket as ReviewBucket));
        // An independently valid semester enrollment on an exception record
        // still counts (e.g. a Fall seat on a record whose August seat was
        // never offered). Everything else routes to Needs Resolution.
        const keepsSemester =
          naturalKey === "Fall Semester" || naturalKey === "Spring Semester" || naturalKey === OCTOBER_TAB;
        // Per-component resolution: closes ONLY this session of the
        // registration (e.g. a never-offered August part). Other components
        // and the canonical seat counts are untouched.
        const foundResolution = findResolution(resolutionIndex, p.registration_uuid, r.label);
        // Refund/credit still owed → stays visible in Needs Resolution with the
        // recorded explanation; only fully settled outcomes move to history.
        const openResolution = foundResolution && isFinanciallyOpen(foundResolution) ? foundResolution : null;
        const componentResolution = openResolution ? null : foundResolution;
        const isException = !componentResolution && !notOperational && (!!openResolution || (!!exception && !keepsSemester));
        const tabKey: RegistrationTabKey = componentResolution
          ? "Historical"
          : isException ? "Needs Resolution" : naturalKey;
        const key = `${tabKey}|${optKey}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const bucket = ensureBucket(tabKey);
        const cls = bucket.classes[groupKey] ??= {
          className: label.title,
          groupKey,
          classIdRaw: rawId,
          isUnknown: label.isUnknown,
          options: {},
        };
        (cls.options[optKey] = cls.options[optKey] || []).push({
          payment: p,
          reasonText: (componentResolution ?? openResolution)
            ? describeResolution((componentResolution ?? openResolution)!)
            : isException
            ? (p.operational_exception_note ??
               "Preserved — this session was not offered. Resolution pending.")
            : notOperational && !r.reasonText
              ? "Refunded / removed — preserved for history"
              : r.reasonText,
          historical: notOperational || r.historical || !!componentResolution,
        });
        // SEAT de-duplication: child + class + resolved period.
        // Exception rows contribute ZERO operational seats.
        if (!notOperational && !isException && !componentResolution) {
          for (const name of childNames) {
            bucket.seats.add(`${tabKey}|${groupKey}|${name.trim().toLowerCase()}`);
          }
        }
      }
    }

    // The primary tabs always exist as views, even when empty. Creating an
    // empty bucket adds NO records and changes no counts.
    for (const p of ["Fall Semester", OCTOBER_TAB, WINTER_TAB, "Needs Resolution"] as RegistrationTabKey[]) {
      if (!buckets[p]) buckets[p] = { period: p, classes: {}, seats: new Set<string>(), total: 0 };
    }

    for (const b of Object.values(buckets)) {
      if (!b) continue;
      // Exception rows hold zero operational seats by design, so the
      // "Needs Resolution" tab counts RECORDS awaiting a decision instead.
      b.total =
        b.period === "Needs Resolution"
          ? new Set(
              Object.values(b.classes).flatMap((c) =>
                Object.values(c.options).flatMap((rows) => rows.map((row) => row.payment.registration_uuid)),
              ),
            ).size
          : b.seats.size;
    }

    const periods = PERIOD_ORDER.filter((p) => !!buckets[p]);
    const counts = periods.map((p) => ({ period: p, count: buckets[p]!.total }));
    return { buckets, periods, counts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, dbSessionsBySlug, resolutionIndex]);

  // ONE primary navigation: Fall Semester 2026 | Winter Semester 2026–2027 |
  // Interest Forms | Needs Resolution. Everything else is archival.

  const stripCounts = [
    ...payView.counts,
    { period: INTEREST_TAB as RegistrationTabKey, count: submissions.length },
  ];
  const activeStripTab: RegistrationTabKey =
    tab === "interest"
      ? INTEREST_TAB
      : ((paymentsPeriod && payView.buckets[paymentsPeriod]
          ? paymentsPeriod
          : payView.periods[0]) as RegistrationTabKey);


  const selectStripTab = (p: RegistrationTabKey) => {
    if (p === INTEREST_TAB) {
      setTab("interest");
      return;
    }
    setTab("payments");
    setPaymentsPeriod(p);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <Tabs value={activeStripTab} onValueChange={(v) => selectStripTab(v as RegistrationTabKey)}>
          <PeriodTabStrip
            counts={stripCounts}
            value={activeStripTab}
            onChange={selectStripTab}
            onDefault={(p) => {
              if (!paymentsPeriod && p && p !== INTEREST_TAB && payView.buckets[p]) setPaymentsPeriod(p);
            }}
          />
        </Tabs>
        <div className="flex items-center gap-2">
          {totalUnread > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead}>
              <CheckCheck className="h-4 w-4 mr-1" /> Mark all read
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchPayments} disabled={loadingPayments}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loadingPayments ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">


        <TabsContent value="payments">
          <Card>
            <CardHeader><CardTitle>Paid Registrations (Stripe)</CardTitle></CardHeader>
            <CardContent>
              {loadingPayments ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : (() => {
                const { buckets, periods } = payView;
                if (!periods.length) return <p className="text-muted-foreground">No registrations yet.</p>;
                const active = paymentsPeriod && buckets[paymentsPeriod]
                  ? paymentsPeriod
                  : periods[0];




                return (
                  <Tabs
                    value={active}
                    onValueChange={(v) => setPaymentsPeriod(v as RegistrationTabKey)}
                    className="space-y-4"
                  >
                    {periods.map((period) => {
                      const bucket = buckets[period]!;
                      const classGroups = Object.values(bucket.classes).sort((a, b) =>
                        a.className.localeCompare(b.className),
                      );

                      return (
                        <TabsContent key={period} value={period}>
                          {classGroups.length === 0 && (
                            <p className="text-sm text-muted-foreground py-6 text-center">
                              {period === WINTER_TAB
                                ? "Winter Semester 2026–2027 offerings are not set up yet, so there are no registrations to show."
                                : `No registrations for ${registrationTabLabel(period)} yet.`}

                            </p>
                          )}
                          <div className="space-y-6">

                            {classGroups.map(({ className, groupKey, classIdRaw, isUnknown, options }) => {
                              const optionEntries = Object.entries(options).sort(([a], [b]) =>
                                a.localeCompare(b),
                              );
                              const classTotal = optionEntries.reduce((s, [, rows]) => s + rows.length, 0);
                              const allFree = optionEntries.every(([, rows]) =>
                                rows.every((r) => (r.payment.amount ?? 0) === 0),
                              );
                              const countLabel = allFree
                                ? `${classTotal} registration${classTotal === 1 ? "" : "s"}`
                                : `${classTotal} registration${classTotal === 1 ? "" : "s"}`;
                              return (
                                <div key={classIdRaw} id={`payclass-${groupKey}`}>
                                  <h3 className="font-heading font-bold text-lg mb-2">
                                    {className}{" "}
                                    <span className="text-sm font-normal text-muted-foreground">
                                      ({countLabel})
                                    </span>
                                    {isUnknown && (
                                      <span className="inline-flex flex-wrap items-center gap-2 ml-2 align-middle text-xs font-normal">
                                        <span className="rounded-full bg-amber-100 text-amber-900 px-2 py-0.5">
                                          Needs identification
                                        </span>
                                        <span className="text-muted-foreground">
                                          Stored ID: <code>{classIdRaw}</code>
                                        </span>
                                        <a
                                          href="/admin/classes"
                                          className="underline text-muted-foreground hover:text-foreground"
                                        >
                                          Assign to class
                                        </a>
                                      </span>
                                    )}
                                  </h3>
                                  <div className="space-y-4 pl-2 border-l-2 border-muted">
                                    {optionEntries.map(([optionKey, group]) => (
                                      <div key={optionKey} className="pl-3">
                                        <div className="text-sm font-semibold mb-1">
                                          {optionKey === "—" ? "All sessions" : optionKey}{" "}
                                          <span className="text-xs font-normal text-muted-foreground">
                                            ({group.length})
                                          </span>
                                        </div>
                                        <div className="overflow-x-auto">
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Parent</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Child</TableHead>
                                                <TableHead>Age</TableHead>
                                                <TableHead>Sessions</TableHead>
                                                <TableHead>Amount</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead></TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>

                                        {group.map(({ payment, reasonText, historical: historicalRow }) => {
                                          const isUnread = !readMarkers.has(markerKey("payment", payment.id));
                                          const kids = payment.children ?? [];
                                          const rows = kids.length > 0
                                            ? kids.map((k, i) => ({
                                                key: `${payment.id}:${i}`,
                                                name: `${k.first_name}${k.last_name ? ` ${k.last_name}` : ""}`,
                                                age: k.age != null ? String(k.age) : "—",
                                                session: k.session_label ?? displaySessions(payment.selected_sessions),
                                                amount: k.amount || Math.round((payment.amount || 0) / kids.length),
                                                isChild: true,
                                                first: i === 0,
                                                needsSplit: false,
                                              }))
                                            : [{
                                                key: payment.id,
                                                name: payment.child_name || "—",
                                                age: payment.child_age || "—",
                                                session: displaySessions(payment.selected_sessions),
                                                amount: payment.amount,
                                                isChild: false,
                                                first: true,
                                                needsSplit: looksLikeLegacyMultiChild(payment.child_name),
                                              }];
                                          return rows.map((r) => (
                                            <TableRow key={r.key} className={isUnread ? "bg-primary/5" : ""}>
                                              <TableCell className="text-sm">
                                                {r.first ? (
                                                  <div className="flex items-center gap-2">
                                                    {isUnread && <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">NEW</Badge>}
                                                    {formatDate(payment.created)}
                                                  </div>
                                                ) : (
                                                  <span className="text-muted-foreground text-xs">↳</span>
                                                )}
                                              </TableCell>
                                              <TableCell className={isUnread && r.first ? "font-semibold" : ""}>
                                                {r.first ? (payment.adult_name || "—") : ""}
                                              </TableCell>
                                              <TableCell>{r.first ? payment.email : ""}</TableCell>
                                              <TableCell>
                                                {r.name}
                                                {r.first && reasonText && (
                                                  <Badge
                                                    variant="outline"
                                                    className={
                                                      historicalRow
                                                        ? "ml-2 text-[10px] text-muted-foreground"
                                                        : "ml-2 text-[10px] border-amber-400 text-amber-700"
                                                    }
                                                  >
                                                    {reasonText}
                                                  </Badge>
                                                )}

                                                {r.needsSplit && (
                                                  <Badge variant="outline" className="ml-2 text-[10px] border-yellow-400 text-yellow-700">
                                                    Needs child details
                                                  </Badge>
                                                )}
                                              </TableCell>
                                              <TableCell>{r.age}</TableCell>
                                              <TableCell className="text-sm">{r.session}</TableCell>
                                              <TableCell className="font-medium">
                                                {payment.is_complimentary ? (
                                                  <div className="space-y-0.5">
                                                    {(payment.original_price_cents ?? 0) > 0 && (
                                                      <div className="text-xs text-muted-foreground line-through">
                                                        {formatAmount(payment.original_price_cents ?? 0)}
                                                      </div>
                                                    )}
                                                    <div>{formatAmount(0)} collected</div>
                                                    {r.first && (
                                                      <Badge variant="secondary" className="text-[10px]">
                                                        100% off · {payment.complimentary_reason || "Complimentary"}
                                                      </Badge>
                                                    )}
                                                  </div>
                                                ) : (
                                                  formatAmount(r.amount)
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {r.first && (
                                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                                    {payment.status}
                                                  </span>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {r.first && (
                                                  <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => isUnread ? markAsRead("payment", payment.id) : markAsUnread("payment", payment.id)} title={isUnread ? "Mark read" : "Mark unread"}>
                                                      <Check className={`h-4 w-4 ${isUnread ? "" : "text-muted-foreground"}`} />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openEditPayment(payment)} title="Edit">
                                                      <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    {isReviewBucket(period) && (
                                                      <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                          setAssignTarget(payment);
                                                          setAssignLabel(displaySessions(payment.selected_sessions) || "");
                                                        }}
                                                      >
                                                        Assign Session
                                                      </Button>
                                                    )}
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => {
                                                        console.info("[registration-delete] trash-click", {
                                                          registration_uuid: payment.registration_uuid,
                                                          stripe_session_id: payment.stripe_session_id ?? null,
                                                          payment_id: payment.registration_uuid,
                                                          class_id: payment.class_id,
                                                          parent_name: payment.adult_name,
                                                        });
                                                        setRemoveTarget(payment);
                                                      }}
                                                      className="text-destructive"
                                                      title="Remove registration"
                                                    >
                                                      <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                  </div>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ));
                                        })}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </TabsContent>
                      );
                    })}
                  </Tabs>
                );
              })()}

            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="interest">
          <Card>
            <CardHeader><CardTitle>Interest Form Submissions</CardTitle></CardHeader>
            <CardContent>
              {loadingData ? (
                <p className="text-muted-foreground">Loading…</p>
              ) : !submissions.length ? (
                <p className="text-muted-foreground">No submissions yet.</p>
              ) : (() => {
                // Period → Class → Schedule option grouping, matching the
                // Paid Registrations tab. Legacy records without a saved
                // option fall into a "Day not selected" bucket that admins
                // can assign inline.
                const GENERAL_INTEREST = "General Interest";
                type Bucket = {
                  period: RegistrationTabKey;
                  classes: Record<string, { className: string; options: Record<string, InterestSubmission[]> }>;
                  total: number;
                };
                const buckets: Partial<Record<RegistrationTabKey, Bucket>> = {};
                for (const s of submissions) {
                  // No vague "Unspecified": an interest form with no usable
                  // period needs a session assigned, and says so.
                  const period: RegistrationTabKey = PERIOD_ORDER.includes(
                    s.enrollment_period as RegistrationTabKey,
                  )
                    ? (s.enrollment_period as RegistrationTabKey)
                    : "Needs Session Assignment";
                  // Non-exclusive: list the child under every option they
                  // selected. The child still counts once toward the period
                  // total (one child, one interest record).
                  const optKeys =
                    s.option_labels && s.option_labels.length > 0
                      ? s.option_labels
                      : [s.schedule_option || s.weekday || GENERAL_INTEREST];
                  // Canonical identity: "db:"-prefixed slugs, bare slugs,
                  // UUIDs and legacy/renamed slugs all collapse to ONE group.
                  const identity = resolveClass(s.class_id);
                  const canonicalId = identity.canonicalKey || s.class_id;
                  const bucket = buckets[period] ?? { period, classes: {}, total: 0 };
                  const cls = bucket.classes[canonicalId] ??= {
                    className: identity.title,
                    options: {},
                  };
                  for (const optKey of optKeys) {
                    (cls.options[optKey] = cls.options[optKey] || []).push(s);
                  }
                  bucket.total += 1;
                  buckets[period] = bucket;
                }
                const nowMonthName = new Intl.DateTimeFormat("en-US", {
                  timeZone: "America/Los_Angeles",
                  month: "long",
                }).format(new Date());
                if ((["May", "June", "July", "August"] as const).includes(nowMonthName as any)) {
                  const p = nowMonthName as EnrollmentPeriod;
                  if (!buckets[p]) buckets[p] = { period: p, classes: {}, total: 0 };
                }
                const periods = PERIOD_ORDER.filter((p) => !!buckets[p]);
                if (!periods.length) return <p className="text-muted-foreground">No submissions yet.</p>;

                return (
                  <div className="space-y-8">
                    {periods.map((period) => {
                      const bucket = buckets[period]!;
                      const classGroups = Object.values(bucket.classes).sort((a, b) =>
                        a.className.localeCompare(b.className),
                      );
                      if (!classGroups.length) return null;
                      return (
                        <div key={period}>
                          <div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                            {period} · {bucket.total} interested
                          </div>
                          <div className="space-y-6">


                            {classGroups.map(({ className, options }) => {
                              const classSessions =
                                classes.find(
                                  (c) => c.title === className || c.id === Object.values(bucket.classes).find(v => v.className === className && v)?.className,
                                )?.sessions ?? [];
                              // Simpler lookup by className match:
                              const cls = classes.find((c) => c.title === className);
                              const availableSessions = cls?.sessions ?? classSessions;
                              const optionEntries = Object.entries(options).sort(([a], [b]) => {
                                if (a === GENERAL_INTEREST) return 1;
                                if (b === GENERAL_INTEREST) return -1;
                                return a.localeCompare(b);
                              });
                              // A child selecting several options appears in
                              // each bucket but counts once for the class.
                              const classTotal = new Set(
                                optionEntries.flatMap(([, rows]) => rows.map((r) => r.id)),
                              ).size;
                              return (
                                <div key={className}>
                                  <h3 className="font-heading font-bold text-lg mb-2">
                                    {className}{" "}
                                    <span className="text-sm font-normal text-muted-foreground">
                                      ({classTotal} interested)
                                    </span>
                                  </h3>
                                  <div className="space-y-4 pl-2 border-l-2 border-muted">
                                    {optionEntries.map(([optionKey, group]) => (
                                      <div key={optionKey} className="pl-3">
                                        <div className="text-sm font-semibold mb-1 flex items-center gap-2">
                                          <span>
                                            {optionKey === GENERAL_INTEREST ? GENERAL_INTEREST : optionKey}
                                          </span>
                                          <span className="text-xs font-normal text-muted-foreground">
                                            ({group.length})
                                          </span>
                                        </div>
                                        <div className="overflow-x-auto">
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Parent</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Child</TableHead>
                                                <TableHead>Age</TableHead>
                                                <TableHead>Topics</TableHead>
                                                {optionKey === GENERAL_INTEREST && availableSessions.length > 1 && (
                                                  <TableHead>Assign</TableHead>
                                                )}
                                                <TableHead></TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {group.map((sub) => {
                                                const isUnread = !readMarkers.has(markerKey("interest", sub.id));
                                                return (
                                                  <TableRow key={sub.id} className={isUnread ? "bg-primary/5" : ""}>
                                                    <TableCell className="text-sm">
                                                      <div className="flex items-center gap-2">
                                                        {isUnread && <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">NEW</Badge>}
                                                        {new Date(sub.created_at).toLocaleDateString()}
                                                      </div>
                                                    </TableCell>
                                                    <TableCell className={isUnread ? "font-semibold" : ""}>{sub.adult_name}</TableCell>
                                                    <TableCell>{sub.email}</TableCell>
                                                    <TableCell>{sub.child_name}</TableCell>
                                                    <TableCell>{sub.child_age}</TableCell>
                                                    <TableCell className="text-sm max-w-[200px] truncate" title={sub.topics ?? undefined}>{sub.topics || "—"}</TableCell>
                                                    {optionKey === GENERAL_INTEREST && availableSessions.length > 1 && (
                                                      <TableCell>
                                                        <select
                                                          className="text-xs border rounded px-1 py-0.5 bg-background"
                                                          defaultValue=""
                                                          onChange={(e) => {
                                                            const v = e.target.value;
                                                            if (v) assignInterestSession(sub, v);
                                                          }}
                                                        >
                                                          <option value="">Assign…</option>
                                                          {availableSessions.map((sess) => (
                                                            <option key={sess.label} value={sess.label}>
                                                              {sess.label}
                                                            </option>
                                                          ))}
                                                        </select>
                                                      </TableCell>
                                                    )}
                                                    <TableCell>
                                                      <div className="flex items-center gap-1">
                                                        <Button variant="ghost" size="sm" onClick={() => isUnread ? markAsRead("interest", sub.id) : markAsUnread("interest", sub.id)}>
                                                          <Check className={`h-4 w-4 ${isUnread ? "" : "text-muted-foreground"}`} />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => openEditInterest(sub)}>
                                                          <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => deleteInterest(sub.id)} className="text-destructive">
                                                          <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                      </div>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                );
              })()}

            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign a canonical session to a registration that never stored one. */}
      <Dialog open={assignTarget !== null} onOpenChange={(o) => { if (!o) { setAssignTarget(null); setAssignLabel(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign session</DialogTitle>
            <DialogDescription>
              {assignTarget?.adult_name || assignTarget?.email} — choose the session this
              registration belongs to. We never guess when more than one session is possible.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={assignLabel}
            onChange={(e) => setAssignLabel(e.target.value)}
            placeholder="e.g. Fall Semester or Wednesdays (August)"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignTarget(null); setAssignLabel(""); }}>Cancel</Button>
            <Button
              disabled={busy || !assignLabel.trim()}
              onClick={() => assignTarget && assignRegistrationSession(assignTarget, assignLabel)}
            >
              Save session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Safe removal — hides/voids, releases the seat, keeps the audit trail. */}
      <Dialog open={removeTarget !== null} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove this registration?</DialogTitle>
            <DialogDescription>
              {removeTarget?.adult_name || removeTarget?.email} will be removed from active
              rosters and dashboards, the seat will be released, and any linked ledger entries
              will be voided. The source record is kept for audit and can be restored.
              Live paid registrations are protected and cannot be deleted here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                if (!removeTarget) return;
                console.info("[registration-delete] confirmation", {
                  registration_uuid: removeTarget.registration_uuid,
                  stripe_session_id: removeTarget.stripe_session_id ?? null,
                  payment_id: removeTarget.registration_uuid,
                  class_id: removeTarget.class_id,
                  parent_name: removeTarget.adult_name,
                });
                void removeRegistration(removeTarget);
              }}
            >
              Remove registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditRegistrationDialog
        open={editMode !== null}
        onOpenChange={(o) => { if (!o) setEditMode(null); }}
        mode={editMode}
        initial={editInitial}
        onSaved={handleEditSaved}
      />
    </>
  );
}
